import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { workspaceAPI } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, BookOpen, Users, FileText, Loader2, FolderOpen, Pencil, Upload, ImageIcon, X, Copy, MoreVertical, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import { DuplicateMoveDialog } from "@/components/DuplicateMoveDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, canManage, canCreateWorkspace, loading: authLoading } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newWorkspace, setNewWorkspace] = useState({ name: "", description: "", thumbnail: "" });
  const [editWorkspace, setEditWorkspace] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [editThumbnailPreview, setEditThumbnailPreview] = useState(null);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  
  // Duplicate dialog state
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetName, setDeleteTargetName] = useState("");
  const [duplicateWorkspaceId, setDuplicateWorkspaceId] = useState(null);
  const [duplicateWorkspaceName, setDuplicateWorkspaceName] = useState('');

  // Wait for auth to be ready before loading workspaces
  useEffect(() => {
    if (!authLoading) {
      loadWorkspaces();
    }
  }, [authLoading]);

  const loadWorkspaces = async (retryCount = 0) => {
    try {
      const response = await workspaceAPI.list();
      setWorkspaces(response.data);
    } catch (error) {
      console.error("Failed to load workspaces:", error);
      // Retry up to 2 times with delay if it's a network/server error
      if (retryCount < 2 && error?.response?.status !== 401) {
        setTimeout(() => loadWorkspaces(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      if (error?.response?.status !== 401) {
        toast.error("Erreur lors du chargement des espaces");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Veuillez sélectionner une image");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("L'image ne doit pas dépasser 5 Mo");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEdit) {
          setEditThumbnailPreview(reader.result);
          setEditWorkspace({ ...editWorkspace, thumbnail: reader.result });
        } else {
          setThumbnailPreview(reader.result);
          setNewWorkspace({ ...newWorkspace, thumbnail: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const clearThumbnail = (isEdit = false) => {
    if (isEdit) {
      setEditThumbnailPreview(null);
      setEditWorkspace({ ...editWorkspace, thumbnail: "" });
      if (editFileInputRef.current) editFileInputRef.current.value = "";
    } else {
      setThumbnailPreview(null);
      setNewWorkspace({ ...newWorkspace, thumbnail: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await workspaceAPI.create(newWorkspace);
      setWorkspaces([...workspaces, response.data]);
      setNewWorkspace({ name: "", description: "", thumbnail: "" });
      setThumbnailPreview(null);
      setDialogOpen(false);
      toast.success("Espace de travail créé !");
      navigate(`/workspace/${response.data.id}`);
    } catch (error) {
      console.error("Failed to create workspace:", error);
      toast.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  // Archive workspace
  const handleArchiveWorkspace = async (e, workspace) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await workspaceAPI.toggleArchive(workspace.id);
      const isArchived = res.data.is_archived;
      setWorkspaces(prev => prev.map(w => w.id === workspace.id ? { ...w, is_archived: isArchived } : w));
      toast.success(isArchived ? "Espace archivé avec succès" : "Espace désarchivé avec succès");
    } catch (error) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  // Delete workspace
  const handleDeleteWorkspace = async () => {
    if (!deleteTargetId) return;
    try {
      await workspaceAPI.delete(deleteTargetId);
      setWorkspaces(prev => prev.filter(w => w.id !== deleteTargetId));
      toast.success("Espace supprimé définitivement");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTargetId(null);
      setDeleteTargetName("");
    }
  };

    const handleEditClick = (e, workspace) => {
    e.preventDefault();
    e.stopPropagation();
    setEditWorkspace({ ...workspace });
    setEditThumbnailPreview(workspace.thumbnail || null);
    setEditDialogOpen(true);
  };

  const handleUpdateWorkspace = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const response = await workspaceAPI.update(editWorkspace.id, {
        name: editWorkspace.name,
        description: editWorkspace.description,
        thumbnail: editWorkspace.thumbnail
      });
      setWorkspaces(workspaces.map(w => w.id === editWorkspace.id ? response.data : w));
      setEditDialogOpen(false);
      setEditWorkspace(null);
      setEditThumbnailPreview(null);
      toast.success("Espace de travail modifié !");
    } catch (error) {
      console.error("Failed to update workspace:", error);
      toast.error("Erreur lors de la modification");
    } finally {
      setUpdating(false);
    }
  };

  const thumbnails = [
    "https://images.unsplash.com/photo-1657127791477-21735aed67ac?crop=entropy&cs=srgb&fm=jpg&w=400",
    "https://images.unsplash.com/photo-1770034285759-236c9fbd97c5?crop=entropy&cs=srgb&fm=jpg&w=400",
    "https://images.unsplash.com/photo-1754738381739-5efb94a4525b?crop=entropy&cs=srgb&fm=jpg&w=400",
  ];

  const ThumbnailUpload = ({ preview, onFileChange, onClear, inputRef, isEdit = false }) => (
    <div className="space-y-2">
      <Label>Image de couverture</Label>
      <div className="flex flex-col gap-3">
        {preview ? (
          <div className="relative aspect-video w-full overflow-hidden rounded-lg border">
            <img src={preview} alt="Preview" className="h-full w-full object-cover" />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={() => onClear(isEdit)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div
            onClick={() => inputRef.current?.click()}
            className="flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50 transition-colors hover:border-primary/50 hover:bg-muted"
          >
            <ImageIcon className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">Cliquez pour ajouter une image</p>
            <p className="text-xs text-muted-foreground/70">PNG, JPG jusqu'à 5 Mo</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) => onFileChange(e, isEdit)}
          className="hidden"
        />
        {!preview && (
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-4 w-4" />
            Choisir une image
          </Button>
        )}
      </div>
    </div>
  );

  // Gradient color palettes for workspace placeholders
  const GRADIENT_PALETTES = [
    { from: "#6366f1", to: "#8b5cf6", accent: "#a78bfa" },  // Indigo → Purple
    { from: "#ec4899", to: "#f43f5e", accent: "#fb7185" },  // Pink → Rose
    { from: "#14b8a6", to: "#06b6d4", accent: "#22d3ee" },  // Teal → Cyan
    { from: "#f59e0b", to: "#ef4444", accent: "#f97316" },  // Amber → Red
    { from: "#8b5cf6", to: "#3b82f6", accent: "#60a5fa" },  // Violet → Blue
    { from: "#10b981", to: "#059669", accent: "#34d399" },  // Emerald → Green
    { from: "#f43f5e", to: "#e11d48", accent: "#fb923c" },  // Rose → Red
    { from: "#3b82f6", to: "#1d4ed8", accent: "#93c5fd" },  // Blue → Dark Blue
    { from: "#06b6d4", to: "#0891b2", accent: "#67e8f9" },  // Cyan → Dark Cyan
    { from: "#d946ef", to: "#a855f7", accent: "#e879f9" },  // Fuchsia → Purple
  ];

  const getWorkspaceGradient = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return GRADIENT_PALETTES[Math.abs(hash) % GRADIENT_PALETTES.length];
  };

  const getInitials = (name) => {
    return name
      .split(/[\s-_]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(w => w[0].toUpperCase())
      .join("");
  };

  const WorkspacePlaceholder = ({ name }) => {
    const palette = getWorkspaceGradient(name);
    const initials = getInitials(name);
    return (
      <div
        className="h-full w-full flex flex-col items-center justify-center relative overflow-hidden transition-transform duration-300 group-hover:scale-105"
        style={{
          background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`,
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-20"
          style={{ background: palette.accent }}
        />
        <div
          className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full opacity-15"
          style={{ background: palette.accent }}
        />
        <div
          className="absolute top-1/2 right-4 w-8 h-8 rounded-full opacity-10"
          style={{ background: "#fff" }}
        />

        {/* Initials circle */}
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
          style={{
            background: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.3)",
          }}
        >
          <span className="text-2xl font-bold text-white tracking-wide">
            {initials}
          </span>
        </div>

        {/* Name */}
        <p
          className="mt-3 text-sm font-medium text-white/90 text-center px-4 line-clamp-1 max-w-[80%]"
          style={{ textShadow: "0 1px 3px rgba(0,0,0,0.2)" }}
        >
          {name}
        </p>
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Bonjour, {user?.first_name} !
            </h1>
            <p className="mt-1 text-muted-foreground">
              {canManage 
                ? "Gérez vos espaces de formation et suivez vos apprenants"
                : "Accédez à vos formations et suivez votre progression"
              }
            </p>
          </div>
          {canCreateWorkspace && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="create-workspace-btn">
                  <Plus className="h-4 w-4" />
                  Nouvel espace
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Créer un espace de travail</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateWorkspace} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ws-name">Nom de l'espace</Label>
                    <Input
                      id="ws-name"
                      placeholder="Ex: Formation Docker"
                      value={newWorkspace.name}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                      required
                      data-testid="workspace-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ws-desc">Description</Label>
                    <Textarea
                      id="ws-desc"
                      placeholder="Décrivez cet espace de formation..."
                      value={newWorkspace.description}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                      rows={3}
                      data-testid="workspace-description-input"
                    />
                  </div>
                  <ThumbnailUpload
                    preview={thumbnailPreview}
                    onFileChange={handleFileChange}
                    onClear={clearThumbnail}
                    inputRef={fileInputRef}
                    isEdit={false}
                  />
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" disabled={creating} data-testid="workspace-submit-btn">
                      {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Créer
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Edit Workspace Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier l'espace de travail</DialogTitle>
            </DialogHeader>
            {editWorkspace && (
              <form onSubmit={handleUpdateWorkspace} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-ws-name">Nom de l'espace</Label>
                  <Input
                    id="edit-ws-name"
                    placeholder="Ex: Formation Docker"
                    value={editWorkspace.name}
                    onChange={(e) => setEditWorkspace({ ...editWorkspace, name: e.target.value })}
                    required
                    data-testid="edit-workspace-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-ws-desc">Description</Label>
                  <Textarea
                    id="edit-ws-desc"
                    placeholder="Décrivez cet espace de formation..."
                    value={editWorkspace.description || ""}
                    onChange={(e) => setEditWorkspace({ ...editWorkspace, description: e.target.value })}
                    rows={3}
                    data-testid="edit-workspace-description-input"
                  />
                </div>
                <ThumbnailUpload
                  preview={editThumbnailPreview}
                  onFileChange={handleFileChange}
                  onClear={clearThumbnail}
                  inputRef={editFileInputRef}
                  isEdit={true}
                />
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={updating} data-testid="edit-workspace-submit-btn">
                    {updating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Enregistrer
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Workspaces Grid */}
        <div className="mt-8">
          <h2 className="section-title">Vos espaces de travail</h2>
          
          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 skeleton rounded-lg" />
              ))}
            </div>
          ) : workspaces.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FolderOpen className="h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 font-semibold">Aucun espace de travail</h3>
                <p className="mt-2 text-sm text-muted-foreground text-center">
                  {canManage 
                    ? "Créez votre premier espace pour commencer à former vos apprenants"
                    : "Vous n'êtes inscrit à aucune formation pour le moment"
                  }
                </p>
                {canCreateWorkspace && (
                  <Button className="mt-4 gap-2" onClick={() => setDialogOpen(true)}>
                    <Plus className="h-4 w-4" />
                    Créer un espace
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {workspaces.map((workspace, index) => (
                <div key={workspace.id} className="relative group">
                  <Link 
                    to={`/workspace/${workspace.id}`}
                    data-testid={`workspace-card-${index}`}
                  >
                    <Card className="h-full overflow-hidden card-hover">
                      <div className="aspect-video w-full overflow-hidden bg-muted" style={workspace.is_archived ? { filter: "grayscale(0.6) opacity(0.7)" } : {}}>
                        {workspace.thumbnail ? (
                          <img
                            src={workspace.thumbnail}
                            alt={workspace.name}
                            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                          />
                        ) : (
                          <WorkspacePlaceholder name={workspace.name} />
                        )}
                      </div>
                      <CardHeader className="pb-2">
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <CardTitle className="line-clamp-1" style={{ flex: 1 }}>{workspace.name}</CardTitle>
                          {workspace.is_archived && (
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
                              padding: "3px 10px", borderRadius: 20,
                              color: "#f59e0b", background: "rgba(245,158,11,0.1)",
                              border: "1px solid rgba(245,158,11,0.3)", whiteSpace: "nowrap"
                            }}>
                              <Archive style={{ width: 12, height: 12 }} />
                              Archivé
                            </span>
                          )}
                        </div>
                        <CardDescription className="line-clamp-2">
                          {workspace.description || "Aucune description"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span>{workspace.participants?.length || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                            <span>{formatDate(workspace.created_at)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  {/* Action Buttons */}
                  {canCreateWorkspace && (
                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-9 w-9 shadow-lg"
                        onClick={(e) => handleEditClick(e, workspace)}
                        data-testid={`edit-workspace-btn-${index}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-9 w-9 shadow-lg"
                            onClick={(e) => e.preventDefault()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.preventDefault();
                              setDuplicateWorkspaceId(workspace.id);
                              setDuplicateWorkspaceName(workspace.name);
                              setDuplicateOpen(true);
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Dupliquer
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleArchiveWorkspace(e, workspace)}>
                            {workspace.is_archived ? (
                              <><ArchiveRestore className="h-4 w-4 mr-2" />Désarchiver</>
                            ) : (
                              <><Archive className="h-4 w-4 mr-2" />Archiver</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeleteTargetId(workspace.id);
                              setDeleteTargetName(workspace.name);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Duplicate Workspace Dialog */}
        {/* Delete Workspace Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'espace de travail</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>"{deleteTargetName}"</strong> ?
              <br /><br />
              <span style={{ color: "#ef4444", fontWeight: 600 }}>
                ⚠️ Cette action est irréversible. Tous les cours, devoirs, messages et fichiers associés seront définitivement supprimés.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWorkspace}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DuplicateMoveDialog
          open={duplicateOpen}
          onOpenChange={setDuplicateOpen}
          mode="duplicate"
          contentType="workspace"
          contentId={duplicateWorkspaceId}
          contentTitle={duplicateWorkspaceName}
          onSuccess={(data) => {
            loadWorkspaces();
            if (data.new_workspace?.id) {
              navigate(`/workspace/${data.new_workspace.id}`);
            }
          }}
        />
      </div>
    </AppLayout>
  );
}
